import { AnnotationLayer, AnnotationMode, type PDFPageProxy, TextLayer } from 'pdfjs-dist'
import { memo, useEffect, useRef, useState } from 'react'
import { createLinkService } from '@/lib/pdf/links'
import { getSource } from '@/lib/pdf/sources'
import { useDoc } from '@/store/documentStore'
import type { PageRef } from '@/types'

/** Cap canvas size: iOS Safari refuses canvases above ~16.7M pixels. */
const MAX_CANVAS_PIXELS = 16_000_000

function usePdfPage(page: PageRef) {
  const [proxy, setProxy] = useState<PDFPageProxy | null>(null)
  useEffect(() => {
    let alive = true
    const src = getSource(page.srcId)
    if (!src) return
    src.pdf.getPage(page.srcIndex + 1).then((p) => alive && setProxy(p))
    return () => {
      alive = false
    }
  }, [page.srcId, page.srcIndex])
  return proxy
}

interface Props {
  page: PageRef
  /** CSS pixels per point */
  scale: number
  /** render text + form layers (only for pages near the viewport) */
  interactive: boolean
  onRendered?: (canvas: HTMLCanvasElement) => void
}

/**
 * The pdf.js-rendered parts of a page: a HiDPI canvas, the selectable text layer, and the
 * form/link layer. Re-rendering is debounced while zooming; the old canvas is stretched until
 * the sharp one is ready, so zooming always feels instant.
 */
export const PageLayers = memo(function PageLayers({ page, scale, interactive, onRendered }: Props) {
  const proxy = usePdfPage(page)
  const holder = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const annRef = useRef<HTMLDivElement>(null)
  const [renderScale, setRenderScale] = useState(scale)
  const markFormChanged = useDoc((s) => s.markFormChanged)

  // debounce the expensive re-render when the zoom changes
  useEffect(() => {
    if (renderScale === scale) return
    const t = setTimeout(() => setRenderScale(scale), 140)
    return () => clearTimeout(t)
  }, [scale, renderScale])

  // canvas
  useEffect(() => {
    if (!proxy || !holder.current) return
    const dpr = Math.min(window.devicePixelRatio || 1, 3)
    let outScale = renderScale * dpr
    const w = page.width * outScale
    const h = page.height * outScale
    if (w * h > MAX_CANVAS_PIXELS) outScale *= Math.sqrt(MAX_CANVAS_PIXELS / (w * h))
    const viewport = proxy.getViewport({ scale: outScale, rotation: page.baseRotation })
    const canvas = document.createElement('canvas')
    canvas.className = 'page-canvas'
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    canvas.setAttribute('aria-hidden', 'true')
    const task = proxy.render({
      canvas,
      viewport,
      annotationMode: AnnotationMode.ENABLE_FORMS,
      annotationCanvasMap: undefined,
    })
    let cancelled = false
    task.promise
      .then(() => {
        if (cancelled || !holder.current) return
        const old = holder.current.querySelector('canvas')
        holder.current.appendChild(canvas)
        if (old) {
          old.width = 0
          old.remove()
        }
        onRendered?.(canvas)
      })
      .catch((e: Error) => {
        if (e?.name !== 'RenderingCancelledException') console.warn(e)
      })
    return () => {
      cancelled = true
      task.cancel()
    }
  }, [proxy, renderScale, page.baseRotation, page.width, page.height, onRendered])

  // free canvas memory when unmounted
  useEffect(
    () => () => {
      const c = holder.current?.querySelector('canvas')
      if (c) c.width = 0
    },
    [],
  )

  // text layer
  useEffect(() => {
    if (!proxy || !interactive || !textRef.current) return
    const container = textRef.current
    container.replaceChildren()
    const viewport = proxy.getViewport({ scale: renderScale, rotation: page.baseRotation })
    const layer = new TextLayer({ textContentSource: proxy.streamTextContent(), container, viewport })
    layer.render().catch(() => {})
    // pdf.js appends an endOfContent div used for nicer selection behaviour
    const eoc = document.createElement('div')
    eoc.className = 'endOfContent'
    container.appendChild(eoc)
    return () => layer.cancel()
  }, [proxy, interactive, renderScale, page.baseRotation])

  // forms + links
  useEffect(() => {
    if (!proxy || !interactive || !annRef.current || !page.srcId) return
    const src = getSource(page.srcId)
    if (!src) return
    const div = annRef.current
    div.replaceChildren()
    let alive = true
    const viewport = proxy
      .getViewport({ scale: renderScale, rotation: page.baseRotation })
      .clone({ dontFlip: true })
    const linkService = createLinkService(page.srcId)
    ;(src.pdf.annotationStorage as unknown as { onSetModified: () => void }).onSetModified = () =>
      markFormChanged()
    proxy.getAnnotations({ intent: 'display' }).then((annotations) => {
      if (!alive || !annotations.length) return
      const layer = new AnnotationLayer({
        div,
        page: proxy,
        viewport,
        linkService: linkService as never,
        annotationStorage: src.pdf.annotationStorage,
        accessibilityManager: null as never,
        annotationCanvasMap: null as never,
        annotationEditorUIManager: null as never,
        structTreeLayer: null as never,
        commentManager: null as never,
      })
      layer
        .render({
          viewport,
          div,
          annotations,
          page: proxy,
          linkService: linkService as never,
          annotationStorage: src.pdf.annotationStorage,
          renderForms: true,
          enableScripting: false,
          hasJSActions: false,
          fieldObjects: null,
          imageResourcesPath: '',
        } as never)
        .catch((e: unknown) => console.warn('annotation layer', e))
    })
    return () => {
      alive = false
    }
  }, [proxy, interactive, renderScale, page.baseRotation, page.srcId, markFormChanged])

  const factor = renderScale === scale ? 1 : scale / renderScale

  return (
    <>
      <div ref={holder} className="absolute inset-0 overflow-hidden" />
      {interactive && (
        <div
          className="absolute top-0 left-0"
          style={{
            transform: factor === 1 ? undefined : `scale(${factor})`,
            transformOrigin: '0 0',
            ['--scale-factor' as string]: renderScale,
            ['--total-scale-factor' as string]: renderScale,
          }}
        >
          <div ref={textRef} className="textLayer" />
          <div ref={annRef} className="annotationLayer" />
        </div>
      )}
    </>
  )
})
